import {getStateColor, getStateTextColor} from "../../../../../../utils/publish";
import {formatDateTimeRange, getTimeText, timeDifference, timeStringToMinutes} from "../calendarUtils";
import {ACTION} from "../calendarActions";

export default class CalendarEvent {
    constructor(instance, activeEventId, privileges, calendarRef) {
        this.instance = instance;
        this.activeEventId = activeEventId;
        /** @type{CalendarPrivileges} */
        this.privileges = privileges;
        this.calendarRef = calendarRef;
    }

    /** @type {CalendarPublishElement} */
    get elementData() {
        return this.instance.event.extendedProps.elementData;
    }

    get title() {
        return this.instance.event.title;
    }

    get timestamp() {
        const {start, end, allDay} = this.instance.event;
        return formatDateTimeRange(start, end, this.calendarRef, allDay);
    }

    get state() {
        if (!this.elementData) return null;
        if (this.elementData.state === 'partsynced') return 'synced';
        return this.elementData.state;
    }

    get loading() {
        return this.elementData?.loading;
    }

    get view() {
        return this.instance.view;
    }

    get isInMonthView() {
        return this.view.type === 'dayGridMonth';
    }

    get isInWeekView() {
        return this.view.type === 'timeGridWeek';
    }

    get isInDayView() {
        return this.view.type === 'timeGridDay';
    }

    get isInListView() {
        return this.view.type === 'listWeek';
    }

    get allDay() {
        return this.instance.event.allDay;
    }

    get active() {
        return this.instance.event.id === this.activeEventId;
    }

    get eventId() {
        return this.instance.event.id;
    }

    get displayInformation() {
        if (!this.instance.timeText) return true;
        if (this.isInMonthView) return true;

        const timeText = getTimeText(this.instance);
        const difference = timeDifference(timeText);
        // console.log(`The time is ${this.instance.timeText} and the difference is ${difference}`);
        return difference >= 15;
    }

    get isBackgroundEvent() {
        return this.instance.event.display === 'background';
    }

    get backgroundColor() {
        if (this.isBackgroundEvent) return this.instance.backgroundColor;
        return getStateColor(this.state);
    }

    get textColor() {
        return getStateTextColor(this.state);
    }

    get containerStyles() {
        return {
            '--calendar-event-background-color': this.backgroundColor,
            '--calendar-event-color': this.textColor,
            // color: this.textColor,
        };
    }

    get elementIconData() {
        return this.elementData?.data;
    }

    get actions() {
        const actions = [];

        if (!this.elementData) return actions;

        const isTemplate = this.elementData.data.type === 'template';
        const isCampaign = this.elementData.data.type === 'campaign';
        const showLinkAction = this.elementData.data.state === 'link';

        actions.push({
            id: ACTION.PREVIEW,
            label: Lang.langGet('Preview'),
            icon: 'far fa-eye'
        })

        if (this.privileges.canEdit() && (isTemplate || isCampaign)) {
            actions.push({
                id: ACTION.EDIT,
                label: Lang.langGet('Edit'),
                icon: 'far fa-pencil'
            });
        }

        if (this.privileges.canEdit() && showLinkAction) {
            actions.push({
                id: ACTION.UNLINK,
                label: Lang.langGet('Create Local Copy'),
                icon: 'far fa-link-simple-slash'
            });
        }

        if (this.privileges.canDelete()) {
            if (this.elementData.deletedAt) {
                actions.push({
                    id: ACTION.UNDELETE,
                    label: Lang.langGet('Undelete'),
                    icon: 'far fa-trash-slash'
                });
            } else {
                actions.push({
                    id: ACTION.DELETE,
                    label: Lang.langGet('Delete'),
                    icon: 'far fa-trash'
                });
            }
        }

        return actions;
    }

    /**
     *
     * @param [onlyTitle] {Boolean}
     * @returns {string}
     */
    getTitleAndTime(onlyTitle) {
        if (onlyTitle) return this.title;

        if (this.isInMonthView && this.instance.timeText) {
            return `${this.instance.timeText} - ${this.title}`;
        }

        return this.title;
    }

    getDisplayExtraInformation(settings = {}) {
        if (this.instance.isDragging) return false;
        if (!this.instance.timeText) return false;
        if (this.isInMonthView) return false;

        const timeText = getTimeText(this.instance);
        const difference = timeDifference(timeText);
        const minutes = timeStringToMinutes(settings.slotDuration);
        return difference > 2 * minutes;
    }

    getDisplayInformation(settings = {}) {
        if (!this.instance.timeText) return true;
        if (this.isInMonthView) return true;

        const timeText = getTimeText(this.instance);
        const difference = timeDifference(timeText);
        // console.log(`The time is ${this.instance.timeText} and the difference is ${difference}`);
        const minutes = timeStringToMinutes(settings.slotDuration);
        return difference > minutes;
    }
}
